import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import ButtonShadow from "./ButtonShadow";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import {
  openBetSelectPopup,
  closeBetSelectPopup
} from "../../../../State/actions";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import {
  selectBetSelectPopupActive,
  selectCriticalAnimationPlaying,
  selectIsSpinning,
  selectAutoPlayActive,
  selectAnyFeatureTriggeredOrActive,
  selectFreeRoundsActive,
  selectReplaySessionActive,
} from "../../../../State/selectors";

// Bet select button configuration - matches UI button dimensions
const BET_SELECT_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  // An iPad upright (layout TABLET_PORTRAIT on the MOBILE device, 2026-10-29):
  // slightly bigger, as in the platform's own games — its places are in its
  // own size, so the gaps grow with it.
  LAYOUT_CONFIGS: {
    // TABLET_PORTRAIT: { deviceMultiplier: 2.14 },
    TABLET_PORTRAIT: { deviceMultiplier: 2.18 }, // a tiny bit larger
  },
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.03
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.575
      }
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.04
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.45
      }
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 0.9
      }
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0
    }
  }
};

const ButtonBetSelect = ({ toolbar }) => {
  const dispatch = useDispatch();
  const popupActive = useSelector(selectBetSelectPopupActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const isSpinning = useSelector(selectIsSpinning);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const anyFeatureTriggeredOrActive = useSelector(
    selectAnyFeatureTriggeredOrActive,
  );
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const replaySessionActive = useSelector(selectReplaySessionActive);
  const { getResponsiveConfig, device, orientation, dimensions, isMobileSmall } =
    useDimensions();



  const texture = getNewUITexture("icon_bet_select");

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(BET_SELECT_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar width instead of full screen width
  const buttonPosition = useMemo(() => {
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: dimensions.width - buttonConfig.width / 1.575,
          y: -toolbar.visualHeight - buttonConfig.height / 2 - buttonConfig.height * 0.1
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width * 0.775,
          y: -dimensions.height / 2 - buttonConfig.height * 0.625
        };
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: dimensions.width - buttonConfig.width / 1.7,
          y: -toolbar.visualHeight - buttonConfig.height / 2 - buttonConfig.height * 0.115
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width * 0.52,
          y: -dimensions.height / 2 - buttonConfig.height * 0.65
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: dimensions.width - buttonConfig.width * 2.0,
          y: -dimensions.height / 5.2 + buttonConfig.height / 2
        };
      } else {
        return {
          x: buttonConfig.width * 5.6,
          y: toolbar.height / 2
        };
      }
    } else {
      return {
        x: toolbar.width - buttonConfig.width,
        y: toolbar.height / 2
      };
    }
  }, [
    device,
    orientation,
    isMobileSmall,
    dimensions.width,
    dimensions.height,
    toolbar.width,
    toolbar.height,
    toolbar.visualHeight,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Calculate if button should be disabled - prevent during spins, critical animations, autoplay, free rounds, or replay
  const isDisabled =
    criticalAnimationPlaying ||
    anyFeatureTriggeredOrActive ||
    isSpinning ||
    autoPlayActive ||
    freeRoundsActive ||
    replaySessionActive;

  // Shared hover/press hook
  const { buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters, alpha, cursor } =
    useButtonBrightness({ disabled: isDisabled });

  const handleClick = () => {
    // Disable button during critical animations or hold and win feature
    if (isDisabled) {
      return;
    }

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // Don't open bet select popup on desktop
    if (device === PixiSettings.DEVICE.DESKTOP) {
      return;
    }

    soundManager.play(SOUND_NAME.button_click);

    // Add press animation
    triggerPress({
        targetWidth: buttonConfig.width,
        targetHeight: buttonConfig.height
      });

    // Toggle bet select popup - close if open, open if closed
    if (popupActive) {
      dispatch(closeBetSelectPopup());
    } else {
      dispatch(openBetSelectPopup());
    }
  };

  // Remove hover effects when button becomes disabled
  useEffect(() => {
    if (isDisabled && buttonRef.current) {
      buttonRef.current.filters = [];    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabled]);

  useEffect(() => () => cleanupFilters(), [cleanupFilters]);

  // Calculate button radius for circular hitbox (matches visual sprite exactly) - memoized
  const buttonRadius = useMemo(() => buttonConfig.width / 2, [buttonConfig.width]);

  if (device === PixiSettings.DEVICE.DESKTOP) {
    return null;
  }

  return (
    <Container name="secondary_button_bet_select" x={buttonPosition.x} y={buttonPosition.y}>
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_bet_select_interactive_area"
        eventMode="static"
        cursor={cursor}
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      {/* Soft drop shadow under it (ButtonShadow, utils/buttonShadow) */}
      <ButtonShadow target={buttonRef} texture={texture} width={buttonConfig.width} height={buttonConfig.height} />
      <Sprite
        ref={buttonRef}
        texture={texture}
        name="button_bet_select_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        alpha={alpha}
      />
    </Container>
  );
};

export default ButtonBetSelect;
